import React, { useMemo } from 'react';

import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Typography,
} from 'antd';

import {
  Campaign,
  AdGroup,
  CampaignAd,
  AdCabinet,
  SmOffer,
} from '../../types';

const { Text } = Typography;

interface DuplicateCampaignModalProps {
  visible: boolean;
  campaign: Campaign | null;
  adGroups: AdGroup[];
  campaignAds: CampaignAd[];
  cabinets: AdCabinet[];
  offers: SmOffer[];
  onClose: () => void;
  onDuplicate: (campaign: Campaign, groups: AdGroup[], ads: CampaignAd[]) => void;
}

let dupNextId = 20000;
const nextId = () => String(dupNextId++);

const DuplicateCampaignModal: React.FC<DuplicateCampaignModalProps> = ({
  visible,
  campaign,
  adGroups,
  campaignAds,
  cabinets,
  offers,
  onClose,
  onDuplicate,
}) => {
  const [form] = Form.useForm();

  const yaCabinets = useMemo(
    () => cabinets.filter((c) => c.platform === 'yandex_direct'),
    [cabinets]
  );

  const sourceGroups = useMemo(
    () => (campaign ? adGroups.filter((g) => g.campaignId === campaign.id) : []),
    [campaign, adGroups]
  );

  const sourceAds = useMemo(
    () => (campaign ? campaignAds.filter((a) => a.campaignId === campaign.id) : []),
    [campaign, campaignAds]
  );

  const handleOk = () => {
    if (!campaign) return;
    form.validateFields().then((values) => {
      const newCampaignId = nextId();

      const newCampaign: Campaign = {
        ...campaign,
        id: newCampaignId,
        name: values.name,
        cabinetId: values.cabinetId,
        offerId: values.offerId,
        weeklyBudget: values.weeklyBudget ?? campaign.weeklyBudget,
        status: 'draft',
        clonedFromId: campaign.id,
        createdAt: new Date().toISOString(),
      };

      const newGroups: AdGroup[] = [];
      const newAds: CampaignAd[] = [];

      sourceGroups.forEach((g) => {
        const newGroupId = nextId();
        newGroups.push({ ...g, id: newGroupId, campaignId: newCampaignId });

        const groupAds = sourceAds.filter((a) => a.adGroupId === g.id);
        groupAds.forEach((a) => {
          newAds.push({ ...a, id: nextId(), adGroupId: newGroupId, campaignId: newCampaignId, status: 'draft' });
        });
      });

      onDuplicate(newCampaign, newGroups, newAds);
      form.resetFields();
    });
  };

  if (!campaign) return null;

  return (
    <Modal
      title="Дублировать кампанию"
      visible={visible}
      onOk={handleOk}
      onCancel={() => { form.resetFields(); onClose(); }}
      okText="Дублировать"
      cancelText="Отмена"
      width={520}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 6, fontSize: 12 }}>
        <Text type="secondary">
          Будет создана копия: {sourceGroups.length} групп, {sourceAds.length} объявлений. Все объявления в статусе «Черновик».
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          name: `${campaign.name} (копия)`,
          cabinetId: campaign.cabinetId,
          offerId: campaign.offerId,
          weeklyBudget: campaign.weeklyBudget,
        }}
      >
        <Form.Item name="name" label="Название" rules={[{ required: true, message: 'Введите название' }]}>
          <Input />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item name="cabinetId" label="Кабинет" rules={[{ required: true }]}>
            <Select>
              {yaCabinets.map((c) => (
                <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="offerId" label="Оффер" rules={[{ required: true }]}>
            <Select>
              {offers.map((o) => (
                <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <Form.Item name="weeklyBudget" label="Бюджет/нед. (₽)">
          <InputNumber style={{ width: '100%' }} min={0} step={1000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DuplicateCampaignModal;
